import { Directive, ElementRef, Renderer2, AfterViewInit, inject, effect } from '@angular/core';

import { IsMobileService } from './is-mobile.service';

/**
 * Directive for mobile devices, to adjust text size and display based on whether the text fits into 2 lines.
 * If the text fits within 2 lines, it will display normally. If it does not fit within 2 lines, it will make the text
 * a single line and allow horizontal scrolling.
 */
@Directive({
  selector: '[ttMobileTextScroll]',
  standalone: true,
})
export class MobileTextScrollDirective implements AfterViewInit {
  private originalLineHeight: string;
  private originalFontSize: string;

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private isMobileService = inject(IsMobileService);

  constructor() {
    effect(() => {
      this.adjustTextSize(this.isMobileService.isMobile());
    });
  }

  public ngAfterViewInit() {
    this.storeOriginalStyles();
    this.adjustTextSize(this.isMobileService.isMobile());
  }

  private storeOriginalStyles() {
    const element = this.el.nativeElement;
    const computedStyle = getComputedStyle(element);

    this.originalLineHeight = computedStyle.lineHeight;
    this.originalFontSize = computedStyle.fontSize;
  }

  private adjustTextSize(isMobile: boolean) {
    const element = this.el.nativeElement as HTMLElement;
    const firstSubElement = element.children[0] as HTMLElement;

    if (isMobile) {
      this.renderer.setStyle(firstSubElement, 'white-space', 'normal');

      if (this.doesTextFitInTwoLines(firstSubElement)) {
        this.renderer.addClass(element, 'tt-mobile-text_fits-two-lines');
      } else {
        this.renderer.addClass(element, 'tt-mobile-text_overflows-two-lines');
      }
    } else {
      this.renderer.removeClass(element, 'tt-mobile-text_fits-two-lines');
      this.renderer.removeClass(element, 'tt-mobile-text_overflows-two-lines');
    }

    this.renderer.removeStyle(firstSubElement, 'white-space');
  }

  private doesTextFitInTwoLines(element: HTMLElement): boolean {
    let lineHeight = parseFloat(this.originalLineHeight);

    if (isNaN(lineHeight)) {
      lineHeight = parseFloat(this.originalFontSize) * 1.2;
    }

    const maxHeight = lineHeight * 2;
    return element.scrollHeight <= maxHeight;
  }
}
